import { createFileRoute, Link } from "@tanstack/react-router";
import { Cake, Users, UtensilsCrossed, Phone } from "lucide-react";
import { canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/small-events-crystal-beach")({
  head: () => ({
    meta: [
      { title: "Small Events and Private Gatherings - Malak BnB Resort" },
      { name: "description", content: "An intimate venue for small events and private gatherings in Crystal Beach, Ontario. Up to 25 guests, poolside setups, and in-house catering at Malak BnB Resort." },
      { property: "og:title", content: "Small Events and Private Gatherings - Malak BnB Resort" },
      { property: "og:description", content: "Birthdays, anniversaries, and small family gatherings by the pool at Malak BnB Resort in Crystal Beach." },
      { property: "og:url", content: "https://malakbnbresort.ca/small-events-crystal-beach" },
      { property: "og:image", content: "/images/events-food.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
    ],
    links: [canonical("/small-events-crystal-beach")],
  }),
  component: SmallEventsPage,
});

export const FAQ = [
  {
    q: "How many guests can Malak BnB Resort host for a small event?",
    a: "Malak BnB Resort hosts small events and private gatherings for up to 25 guests, a size suited to birthdays, anniversaries, and intimate family celebrations rather than large receptions.",
  },
  {
    q: "What kind of small events does Malak BnB Resort host?",
    a: "Malak BnB Resort regularly hosts birthday celebrations, anniversaries, small family gatherings, and poolside get-togethers, alongside weddings, graduations, and corporate retreats through the property's full events service.",
  },
  {
    q: "Is catering available for small events at Malak BnB Resort?",
    a: "Yes. In-house catering is available for small events at Malak BnB Resort, including kosher, Middle Eastern, and Italian-inspired options.",
  },
  {
    q: "How do I plan a small event at Malak BnB Resort?",
    a: "Reach out through the contact page with your date, guest count, and the kind of gathering you are planning. The Malak BnB Resort team will work with you directly on the details rather than through an automated booking system.",
  },
];

function SmallEventsPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/events-food.webp"
          alt="Table setup for a small private gathering at Malak BnB Resort"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Crystal Beach, Ontario</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Small Events and Private Gatherings in Crystal Beach
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">Something Smaller and More Personal</p>
          <p className="text-2xl font-heading text-foreground leading-snug mb-8">
            Not every celebration needs a ballroom. Some just need good food, a pool, and people who matter.
          </p>
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              A milestone birthday, a small anniversary dinner, a family reunion that does not need a hundred
              chairs. Malak BnB Resort in Crystal Beach hosts exactly this kind of gathering: up to 25 guests,
              poolside if the weather allows, with the same family-owned attention that goes into every stay at the
              property.
            </p>
            <p>
              If you are planning something larger, like a wedding or a corporate retreat, the full{" "}
              <Link to="/meetings-and-events" className="text-primary font-semibold hover:underline">
                Meetings and Events
              </Link>{" "}
              page covers the wider venue in more detail.
            </p>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-background rounded-2xl p-6 border border-border/50 text-center">
              <Users className="h-6 w-6 text-primary mx-auto mb-3" />
              <p className="font-heading text-lg text-foreground">Up to 25 Guests</p>
              <p className="text-sm text-muted-foreground mt-1">Sized for intimate gatherings, not large receptions</p>
            </div>
            <div className="bg-background rounded-2xl p-6 border border-border/50 text-center">
              <Cake className="h-6 w-6 text-primary mx-auto mb-3" />
              <p className="font-heading text-lg text-foreground">Birthdays and Anniversaries</p>
              <p className="text-sm text-muted-foreground mt-1">Poolside setups for family milestones</p>
            </div>
            <div className="bg-background rounded-2xl p-6 border border-border/50 text-center">
              <UtensilsCrossed className="h-6 w-6 text-primary mx-auto mb-3" />
              <p className="font-heading text-lg text-foreground">In-House Catering</p>
              <p className="text-sm text-muted-foreground mt-1">Kosher, Middle Eastern, and Italian-inspired options</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/events-setup.webp" alt="Elegant table setup for a private gathering at Malak BnB Resort" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/events-stairway.webp" alt="Grand staircase at Malak BnB Resort set up for a small event" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Planning Your Gathering</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Plan your small event at Malak BnB Resort
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Reach out with your date and guest count and the team will work with you directly on the details.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/contact" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Ask About Availability
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
